// <pos-customer-checkout/> — QR 顧客送單確認 + 成功頁
// 路徑：#/order/:slug/:table/checkout
// 從 sessionStorage 讀 cart + 從 fetchPublicMenu 取品項資料 + createPublicOrder
// see src/designs/customer-checkout.html

import { fetchPublicMenu, createPublicOrder } from '../lib/api.js';
import { t, lang } from '../lib/i18n.js';
import { getFreshSessionToken, clearSession } from '../lib/qr-session-store.js';

const CART_KEY = 'pos-qr-cart';

function parseRouteParams() {
  const m = location.hash.match(/^#\/order\/([^\/?]+)\/([^\/?]+)/);
  return m ? { slug: decodeURIComponent(m[1]), table: decodeURIComponent(m[2]) } : {};
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

class PosCustomerCheckout extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      loading: true,
      error: null,
      items: [],       // resolved {id, name, price, qty, lineTotal}
      tenant: null,
      customerNote: '',
      submitting: false,
      done: null,      // { order_number, total } 送單成功後顯示
    };
  }

  async connectedCallback() {
    this.render();
    await this.load();
  }

  async load() {
    const { slug } = parseRouteParams();
    const cart = this.readCart();
    const ids = Object.keys(cart);
    if (!slug || ids.length === 0) {
      this.state.loading = false;
      this.state.error = t('購物車是空的');
      this.render();
      return;
    }
    try {
      const data = await fetchPublicMenu(slug);
      this.state.tenant = data.tenant;
      this.state.items = (data.items || [])
        .filter(i => cart[i.id])
        .map(i => ({
          id: i.id,
          name: i.name,
          price: Number(i.price),
          qty: cart[i.id],
          lineTotal: Number(i.price) * cart[i.id],
        }));
    } catch (err) {
      this.state.error = (lang() === 'zh' ? err.message_zh : err.message_en) || err.message || t('載入失敗');
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  readCart() {
    try { return JSON.parse(sessionStorage.getItem(CART_KEY) || '{}'); }
    catch { return {}; }
  }
  clearCart() {
    sessionStorage.removeItem(CART_KEY);
  }

  totals() {
    const subtotal = this.state.items.reduce((a, b) => a + b.lineTotal, 0);
    return { subtotal, total: subtotal };  // tax 先不顯示給顧客看（伺服器會算）
  }

  async onSubmit() {
    if (this.state.submitting) return;
    this.state.submitting = true;
    this.state.error = null;
    this.render();

    try {
      const { slug, table } = parseRouteParams();
      const items = this.state.items.map(i => ({
        menu_item_id: i.id,
        qty: i.qty,
      }));
      // C8 方案 A：送單前換 session token（5min 短期 jti）
      // 沒 long token → qrToken=null（demo/未啟用 require_qr_token 的 tenant 適用）
      let qrToken = null;
      try {
        qrToken = await getFreshSessionToken(slug, table);
      } catch (e) {
        // long token 失效（過期/被竄改）→ 走無 token 路徑；
        // 若 tenant.require_qr_token=true 後端會回 403，前端錯誤訊息會自然顯示
        console.warn('[customer-checkout] qr session exchange failed', e);
      }
      const res = await createPublicOrder({
        slug,
        tableNumber: table,
        items,
        customerNote: this.state.customerNote?.trim() || undefined,
        qrToken: qrToken || undefined,
      });
      this.state.done = { ...res.order, success_message: res.success_message || null };
      this.clearCart();
      clearSession(slug, table); // 訂單成功 → 釋出 session cache（下次重新換）
    } catch (err) {
      this.state.error = (lang() === 'zh' ? err.message_zh : err.message_en) || err.message || t('送單失敗，請稍後再試');
    } finally {
      this.state.submitting = false;
      this.render();
    }
  }

  render() {
    if (this.state.done) return this.renderSuccess();
    return this.renderConfirm();
  }

  renderConfirm() {
    const { loading, error, items, submitting } = this.state;
    const { subtotal, total } = this.totals();
    const { slug, table } = parseRouteParams();

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>

      <header class="topbar">
        <button class="topbar__back" aria-label="${t('返回菜單')}" data-act="back">←</button>
        <h1 class="topbar__title">${t('確認訂單')}</h1>
      </header>

      ${loading ? `<div class="full-msg">${t('載入中…')}</div>` : error && items.length === 0 ? `
        <div class="full-msg">
          <div class="full-msg__icon">🛒</div>
          <div>${escapeHtml(error)}</div>
          <button class="btn-link" data-act="back">${t('返回菜單')}</button>
        </div>
      ` : `
        <section class="summary">

          <div class="summary__group">
            <div class="summary__title">${t('訂單明細')}（${t('桌號')} ${escapeHtml(table || '-')}）</div>
            ${items.map(i => `
              <div class="line-item">
                <div>
                  <div class="line-item__name">${escapeHtml(i.name)}</div>
                  <div class="line-item__qty">NT$ ${i.price} × ${i.qty}</div>
                </div>
                <div class="line-item__sub">NT$ ${i.lineTotal}</div>
              </div>
            `).join('')}
            <div class="total-row">
              <span class="total-row__label">${t('總計')}</span>
              <span class="total-row__amount">NT$ ${total}</span>
            </div>
          </div>

          <div class="summary__group note-field">
            <div class="summary__title">${t('備註（選填）')}</div>
            <textarea id="note" placeholder="${t('例：少冰、不加糖、等朋友到再上')}">${escapeHtml(this.state.customerNote)}</textarea>
            <div class="note-field__hint">${t('送單後可能無法修改，如有客製需求請寫清楚')}</div>
          </div>

        </section>

        ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}

        <div class="submit-bar">
          <button class="btn-submit" data-act="submit" ${submitting ? 'disabled' : ''}>
            ${submitting ? t('送單中…') : t('送出訂單')}
          </button>
        </div>
      `}
    `;

    this.wireEvents();
  }

  renderSuccess() {
    const { done } = this.state;
    const { table } = parseRouteParams();

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>
      <section class="success">
        <div class="success__check">✓</div>
        <h1 class="success__title">${t('訂單送出成功')}</h1>
        <div class="success__code">#${escapeHtml(done.order_number || '—')}</div>
        <p class="success__hint">${done.success_message ? escapeHtml(done.success_message) : t('店員會盡快準備')}<br>${t('餐點會送到')} <strong>${escapeHtml(table || '-')}</strong>${t('號桌')}</p>
        <button class="btn-link" data-act="back">${t('返回菜單繼續點')}</button>
      </section>
    `;
    this.wireEvents();
  }

  wireEvents() {
    const root = this.shadowRoot;
    root.querySelectorAll('[data-act]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const act = btn.dataset.act;
        if (act === 'back') {
          const { slug, table } = parseRouteParams();
          if (slug && table) location.hash = `#/order/${encodeURIComponent(slug)}/${encodeURIComponent(table)}`;
          else location.hash = '#/';
        } else if (act === 'submit') {
          this.onSubmit();
        }
      });
    });
    const noteEl = root.querySelector('#note');
    if (noteEl) {
      noteEl.addEventListener('input', (e) => {
        this.state.customerNote = e.target.value;
      });
    }
  }

  styles() {
    return `
      :host { display: block; font-family: var(--font-sans); background: var(--bg-base); color: var(--fg-base); min-height: 100vh; padding-bottom: 120px; }
      .full-msg { padding: var(--s-16) var(--s-5); text-align: center; color: var(--fg-muted); display: grid; gap: var(--s-4); place-content: center; min-height: 60vh; }
      .full-msg__icon { font-size: 3rem; opacity: .5; }

      .topbar { display: flex; align-items: center; gap: var(--s-3); padding: var(--s-4) var(--s-5); background: var(--bg-surface); border-bottom: 1px solid var(--border); }
      .topbar__back { width: 36px; height: 36px; background: transparent; border: 1px solid var(--border); border-radius: var(--r-full); color: var(--fg-base); font-size: 1.2rem; cursor: pointer; }
      .topbar__title { font-size: 1.05rem; font-weight: 600; margin: 0; }

      .summary { padding: var(--s-5); display: grid; gap: var(--s-4); }
      .summary__group { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); padding: var(--s-4) var(--s-5); }
      .summary__title { color: var(--fg-muted); font-size: .75rem; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: var(--s-3); }

      .line-item { display: grid; grid-template-columns: 1fr auto; gap: var(--s-3); padding: var(--s-2) 0; border-bottom: 1px dashed var(--border); }
      .line-item:last-child { border-bottom: 0; }
      .line-item__name { font-weight: 500; }
      .line-item__qty { font-size: .85rem; color: var(--fg-muted); margin-top: 2px; }
      .line-item__sub { font-family: var(--font-mono); color: var(--brand-primary); align-self: center; }

      .total-row { display: flex; justify-content: space-between; align-items: baseline; padding-top: var(--s-4); margin-top: var(--s-3); border-top: 1px solid var(--border-strong); }
      .total-row__label { color: var(--fg-muted); font-size: .9rem; }
      .total-row__amount { font-family: var(--font-mono); font-size: 1.5rem; font-weight: 700; color: var(--brand-accent); }

      .note-field textarea { width: 100%; resize: vertical; min-height: 88px; background: var(--bg-base); color: var(--fg-base); border: 1px solid var(--border); border-radius: var(--r-md); padding: var(--s-3); font-family: var(--font-sans); font-size: .95rem; line-height: 1.5; box-sizing: border-box; }
      .note-field textarea:focus { outline: none; border-color: var(--brand-primary); }
      .note-field__hint { color: var(--fg-dim); font-size: .75rem; margin-top: var(--s-2); }

      .banner { margin: 0 var(--s-5); padding: var(--s-3) var(--s-4); border-radius: var(--r-md); }
      .banner.err { background: rgba(239, 68, 68, 0.1); color: var(--danger); }

      .submit-bar { position: fixed; left: 0; right: 0; bottom: 0; padding: var(--s-4) var(--s-5) calc(var(--s-4) + env(safe-area-inset-bottom)); background: var(--bg-surface); border-top: 1px solid var(--border); }
      .btn-submit { width: 100%; height: var(--touch-cta); background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%); color: var(--fg-on-brand); border: none; border-radius: var(--r-lg); font-size: 1.05rem; font-weight: 700; letter-spacing: 1px; cursor: pointer; }
      .btn-submit:active { opacity: .85; }
      .btn-submit:disabled { opacity: .4; cursor: not-allowed; }

      .success { text-align: center; padding: var(--s-16) var(--s-5); display: grid; gap: var(--s-5); justify-items: center; }
      .success__check { width: 96px; height: 96px; border-radius: var(--r-full); background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%); display: flex; align-items: center; justify-content: center; font-size: 3rem; color: #FFFFFF; box-shadow: 0 0 40px rgba(232, 88, 12, 0.4); }
      .success__title { font-family: var(--font-display); font-size: 1.6rem; color: var(--brand-primary); margin: 0; }
      .success__code { font-family: var(--font-mono); font-size: 1.1rem; color: var(--fg-muted); background: var(--bg-elevated); padding: var(--s-2) var(--s-4); border-radius: var(--r-md); border: 1px solid var(--border); }
      .success__hint { color: var(--fg-muted); font-size: .9rem; max-width: 280px; line-height: 1.6; margin: 0; }
      .btn-link { background: transparent; color: var(--brand-primary); border: 1px solid var(--brand-primary); padding: var(--s-3) var(--s-6); border-radius: var(--r-md); cursor: pointer; font-size: .9rem; font-family: inherit; }
    `;
  }
}

customElements.define('pos-customer-checkout', PosCustomerCheckout);
